/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Input,
  Pagination,
  Table,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseCustomerType, useGetCases } from 'api/useCasesApi';
import {
  useDeleteCustomerFile,
  useGetCustomerFiles,
  useUploadCustomerFile,
} from 'api/useFCUCustomerFilesApi';
import {
  useAddCustomerNote,
  useGetCustomerNotes,
} from 'api/useFCUCustomerNotesApi';
import CustomerCm1History from 'neo-office/CustomerCm1History';
import { CaseNotesCell } from './CaseNotesCell';
import { CreateCaseModal } from './CreateCaseModal';
import { TruncatedString } from './TruncatedString';

interface Props {
  customerId: string;
  customerType: CaseCustomerType;
}

export const FCUCards = ({ customerId, customerType }: Props) => {
  const { spacing, palette } = useTheme();
  const session = useSession();
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  // Customer profiles are an audit history, so keep investigated/dismissed
  // sanction cases visible even though the main FCU queue suppresses them.
  const [casesPage, setCasesPage] = useState(1);
  const {
    data: cases,
    isPending: casesLoading,
    isError: casesError,
  } = useGetCases({
    page: casesPage,
    size: 50,
    customerId,
    includeNoiseSanctions: true,
    includePostponed: true,
  });
  const {
    data: notes,
    isPending: notesLoading,
    isError: notesError,
  } = useGetCustomerNotes(customerId);
  const {
    data: customerFiles,
    isPending: filesLoading,
    isError: filesError,
  } = useGetCustomerFiles(customerId);
  const addNoteMutation = useAddCustomerNote(customerId);
  const uploadFileMutation = useUploadCustomerFile(customerId);
  const deleteFileMutation = useDeleteCustomerFile(customerId);
  const [note, setNote] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const hasEditPermission =
    session.authenticated && session.permissions.includes('edit_qb_users');

  return (
    <>
      {session.authenticated &&
        session.permissions.some((permission) =>
          ['view_fcu_info', 'view_neo_office'].includes(permission)
        ) && (
          <CustomerCm1History
            key={`${customerType}-${customerId}`}
            customerType={customerType}
            customerId={customerId}
          />
        )}
      <Card css={{ marginTop: spacing.xxs }}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="bodyBold">Cases</Typography>
          {hasEditPermission && (
            <UtilityButton
              text="Create Case"
              onClick={() => setShowCreateCaseModal(true)}
            />
          )}
        </div>
        {casesLoading && <p role="status">Loading cases…</p>}
        {casesError && (
          <p role="alert">Unable to load customer cases. Please try again.</p>
        )}
        {cases && !cases.cases.length && <p>No customer cases.</p>}
        {cases && cases.cases.length > 0 && (
          <Table
            headers={[
              { cellKey: 'createdAt', label: 'Created At' },
              { cellKey: 'title', label: 'Title' },
              { cellKey: 'uuid', label: 'Case ID' },
              { cellKey: 'state', label: 'State' },
              { cellKey: 'notes', label: 'Notes' },
            ]}
            rows={cases.cases.map((caseItem) => ({
              id: caseItem.uuid,
              value: {
                caseId: caseItem.uuid,
                title: caseItem.title,
                uuid: <TruncatedString text={caseItem.uuid} />,
                createdAt: new Date(caseItem.createdAt).toLocaleString(),
                notes: <CaseNotesCell notes={caseItem.notes} />,
                state: (
                  <Typography variant="body">{`${caseItem.state} ${
                    caseItem.wakeupAt
                      ? `(Postponed until ${new Date(
                          caseItem.wakeupAt
                        ).toLocaleString()})`
                      : ''
                  }`}</Typography>
                ),
              },
            }))}
            onRowClick={(row) => navigate(`/fcu/cases/${row.caseId as string}`)}
          />
        )}
        {cases && cases.pagination.total > cases.pagination.size && (
          <Pagination
            currentPage={casesPage}
            onChange={setCasesPage}
            numberOfElements={cases.pagination.total}
            pageSize={cases.pagination.size}
          />
        )}
      </Card>
      <Card css={{ marginTop: spacing.xxs }}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="bodyBold">Files</Typography>
          {hasEditPermission && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                css={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    uploadFileMutation.mutate(file);
                    e.target.value = '';
                  }
                }}
              />
              <UtilityButton
                text="Upload File"
                onClick={() => fileInputRef.current?.click()}
                loading={uploadFileMutation.isPending}
                disabled={uploadFileMutation.isPending}
              />
            </>
          )}
        </div>
        {filesLoading && <p role="status">Loading files…</p>}
        {filesError && (
          <p role="alert">Unable to load customer files. Please try again.</p>
        )}
        {customerFiles && !customerFiles.files.length && (
          <p>No customer files.</p>
        )}
        {customerFiles && customerFiles.files.length > 0 && (
          <Table
            headers={[
              { cellKey: 'originalFilename', label: 'Filename' },
              { cellKey: 'createdBy', label: 'Uploaded By' },
              { cellKey: 'createdAt', label: 'Uploaded At' },
              { cellKey: 'actions', label: '' },
            ]}
            rows={customerFiles.files.map((file) => ({
              id: file.uuid,
              value: {
                originalFilename: (
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    css={{ color: palette.primary.default }}
                  >
                    {file.originalFilename}
                  </a>
                ),
                createdBy: file.createdBy,
                createdAt: new Date(file.createdAt).toLocaleString(),
                actions: hasEditPermission ? (
                  <UtilityButton
                    text="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFileMutation.mutate(file.uuid);
                    }}
                    loading={deleteFileMutation.isPending}
                    disabled={deleteFileMutation.isPending}
                  />
                ) : null,
              },
            }))}
          />
        )}
      </Card>
      <Card css={{ marginTop: spacing.xxs }}>
        <Typography variant="bodyBold">Customer Notes</Typography>
        {notesLoading && <p role="status">Loading notes…</p>}
        {notesError && (
          <p role="alert">Unable to load customer notes. Please try again.</p>
        )}
        {notes && !notes.notes.length && <p>No customer notes.</p>}
        {notes && notes.notes.length > 0 && (
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xxs,
              marginTop: spacing.xxs,
            }}
          >
            {notes.notes.map((note, index) => (
              <div
                key={index}
                css={{
                  padding: spacing.xxs,
                  backgroundColor: palette.grey.x50,
                  borderRadius: 8,
                  borderLeft: `3px solid ${palette.primary.default}`,
                }}
              >
                <Typography variant="body">{note.content}</Typography>
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: spacing.xxxs,
                  }}
                >
                  <Typography variant="caption" color={palette.grey.x400}>
                    {note.createdBy}
                  </Typography>
                  <Typography variant="caption" color={palette.grey.x400}>
                    {new Date(note.createdAt).toLocaleString()}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasEditPermission && (
          <div
            css={{
              display: 'grid',
              gap: spacing.xxxs,
              marginTop: spacing.xxxs,
            }}
          >
            <div>
              <Typography variant="bodySmallest" color={palette.grey.x500}>
                Add a note
              </Typography>
              <Input
                type="textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Write your note here..."
                disabled={addNoteMutation.isPending}
                rows={2}
              />
            </div>
            <UtilityButton
              text="Add Note"
              onClick={() => {
                addNoteMutation.mutate(
                  {
                    content: note.trim(),
                    customerId: customerId,
                    customerType: customerType,
                  },
                  { onSuccess: () => setNote('') }
                );
              }}
              loading={addNoteMutation.isPending}
              disabled={addNoteMutation.isPending || !note.trim()}
            />
          </div>
        )}
        {addNoteMutation.isError && (
          <p role="alert">
            Unable to save the note. Your text has been preserved; please try
            again.
          </p>
        )}
        {showCreateCaseModal && (
          <CreateCaseModal
            onClose={() => setShowCreateCaseModal(false)}
            customerId={customerId}
            customerType={customerType}
          />
        )}
      </Card>
    </>
  );
};
